import React, { useState, useEffect } from 'react';
import { Globe, Plus, Edit, Trash2, Search, Save, X, Eye, EyeOff, MoveUp, MoveDown, Upload } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { getPublicFileUrl } from '../../../lib/storage';

interface WebsiteServiceCard {
  id: string;
  service_key: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  display_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface GeneralFile {
  id: string;
  file_name: string;
  storage_path: string;
  file_type: string;
  category: string;
}

const WebsiteServices = () => {
  const [services, setServices] = useState<WebsiteServiceCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingService, setEditingService] = useState<WebsiteServiceCard | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generalFiles, setGeneralFiles] = useState<GeneralFile[]>([]);
  const [showFilePicker, setShowFilePicker] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !editingService) return;

    const file = e.target.files[0];
    setUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `service-${editingService.service_key}-${Date.now()}.${fileExt}`;
      const filePath = `services/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('public-files')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const publicUrl = getPublicFileUrl(filePath);
      setEditingService({ ...editingService, image_url: publicUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    fetchServices();
    fetchGeneralFiles();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('website_service_cards')
        .select('*')
        .order('display_order');

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralFiles = async () => {
    try {
      const { data, error } = await supabase
        .from('general_files')
        .select('id, file_name, storage_path, file_type, category')
        .ilike('file_type', 'image/%')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGeneralFiles(data || []);
    } catch (error) {
      console.error('Error fetching general files:', error);
    }
  };

  const handleSave = async () => {
    if (!editingService) return;

    try {
      const { error } = await supabase
        .from('website_service_cards')
        .update({
          title: editingService.title || null,
          description: editingService.description || null,
          image_url: editingService.image_url || null,
          active: editingService.active,
          display_order: editingService.display_order,
        })
        .eq('id', editingService.id);

      if (error) throw error;

      await fetchServices();
      setShowModal(false);
      setEditingService(null);
    } catch (error) {
      console.error('Error updating service:', error);
      alert('Failed to update service');
    }
  };

  const handleToggleActive = async (service: WebsiteServiceCard) => {
    try {
      const { error } = await supabase
        .from('website_service_cards')
        .update({ active: !service.active })
        .eq('id', service.id);

      if (error) throw error;
      await fetchServices();
    } catch (error) {
      console.error('Error toggling service:', error);
      alert('Failed to toggle service visibility');
    }
  };

  const handleMoveUp = async (service: WebsiteServiceCard, index: number) => {
    if (index === 0) return;

    const prevService = services[index - 1];

    try {
      await supabase
        .from('website_service_cards')
        .update({ display_order: prevService.display_order })
        .eq('id', service.id);

      await supabase
        .from('website_service_cards')
        .update({ display_order: service.display_order })
        .eq('id', prevService.id);

      await fetchServices();
    } catch (error) {
      console.error('Error reordering services:', error);
    }
  };

  const handleMoveDown = async (service: WebsiteServiceCard, index: number) => {
    if (index === services.length - 1) return;

    const nextService = services[index + 1];

    try {
      await supabase
        .from('website_service_cards')
        .update({ display_order: nextService.display_order })
        .eq('id', service.id);

      await supabase
        .from('website_service_cards')
        .update({ display_order: service.display_order })
        .eq('id', nextService.id);

      await fetchServices();
    } catch (error) {
      console.error('Error reordering services:', error);
    }
  };

  const filteredServices = services.filter(service =>
    service.service_key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Globe className="text-blue-600" />
              Website Service Cards
            </h2>
            <p className="text-gray-600 mt-1">Manage service cards displayed on the public website</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredServices.map((service, index) => (
              <div key={service.id} className="border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                {service.image_url && (
                  <img
                    src={service.image_url}
                    alt={service.service_key}
                    className="w-20 h-20 object-cover rounded"
                  />
                )}

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{service.service_key}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      service.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {service.active ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  {service.title && (
                    <p className="text-sm text-gray-700 mb-1">Title Override: {service.title}</p>
                  )}
                  {service.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{service.description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Display Order: {service.display_order}</p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleMoveUp(service, index)}
                    disabled={index === 0}
                    className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move Up"
                  >
                    <MoveUp size={18} />
                  </button>
                  <button
                    onClick={() => handleMoveDown(service, index)}
                    disabled={index === services.length - 1}
                    className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move Down"
                  >
                    <MoveDown size={18} />
                  </button>
                  <button
                    onClick={() => handleToggleActive(service)}
                    className="p-2 text-blue-600 hover:text-blue-800"
                    title={service.active ? 'Hide' : 'Show'}
                  >
                    {service.active ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingService(service);
                      setShowModal(true);
                    }}
                    className="p-2 text-blue-600 hover:text-blue-800"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && editingService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Edit Service Card</h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingService(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Key
                  </label>
                  <input
                    type="text"
                    value={editingService.service_key}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">This uses translation keys from the locales files</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title Override (Optional)
                  </label>
                  <input
                    type="text"
                    value={editingService.title || ''}
                    onChange={(e) => setEditingService({ ...editingService, title: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty to use translation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description Override (Optional)
                  </label>
                  <textarea
                    value={editingService.description || ''}
                    onChange={(e) => setEditingService({ ...editingService, description: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Leave empty to use translation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL or Upload
                  </label>
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={editingService.image_url || ''}
                      onChange={(e) => setEditingService({ ...editingService, image_url: e.target.value || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                    />
                    <div className="flex items-center gap-2">
                      <label className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-center">
                        <Upload size={16} className="inline mr-2" />
                        {uploadingImage ? 'Uploading...' : 'Upload New Image'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowFilePicker(!showFilePicker)}
                        className="flex-1 bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors text-center"
                      >
                        {showFilePicker ? 'Hide' : 'Choose from Files'}
                      </button>
                    </div>
                  </div>
                  {showFilePicker && generalFiles.length > 0 && (
                    <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                      <div className="grid grid-cols-3 gap-2">
                        {generalFiles.map((file) => (
                          <div
                            key={file.id}
                            onClick={() => {
                              setEditingService({ ...editingService, image_url: getPublicFileUrl(file.storage_path) });
                              setShowFilePicker(false);
                            }}
                            className="cursor-pointer border-2 border-transparent hover:border-blue-500 rounded overflow-hidden transition-all"
                          >
                            <img
                              src={getPublicFileUrl(file.storage_path)}
                              alt={file.file_name}
                              className="w-full h-24 object-cover"
                            />
                            <p className="text-xs text-gray-600 p-1 truncate">{file.file_name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {showFilePicker && generalFiles.length === 0 && (
                    <p className="mt-3 text-sm text-gray-500 text-center p-4 border border-gray-200 rounded-lg">
                      No images found in general files. Upload images via the Files module first.
                    </p>
                  )}
                  {editingService.image_url && (
                    <img
                      src={editingService.image_url}
                      alt="Preview"
                      className="mt-2 w-full h-40 object-cover rounded"
                    />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={editingService.active}
                    onChange={(e) => setEditingService({ ...editingService, active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-700">
                    Show on website
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingService(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteServices;
